export type AwardType = 'NEED' | 'AGI' | 'SAI';
export type CalculatedBy = 'PERCENTAGE' | 'FLAT_AMOUNT';

export const AWARD_TYPE_OPTIONS: Array<{ value: AwardType; label: string }> = [
  { value: 'NEED', label: 'Need' },
  { value: 'AGI', label: 'AGI' },
  { value: 'SAI', label: 'SAI' },
];

export const CALCULATED_BY_OPTIONS: Array<{
  value: CalculatedBy;
  label: string;
}> = [
  { value: 'PERCENTAGE', label: '%' },
  { value: 'FLAT_AMOUNT', label: 'Flat amount' },
];

export const RANGES_LABEL_BY_AWARD_TYPE: Record<AwardType, string> = {
  NEED: 'Need ranges',
  AGI: 'AGI ranges',
  SAI: 'SAI ranges',
};

export const DEFAULT_ACADEMIC_RATES = [1, 2, 3, 4, 5];

export const SECONDARY_LABEL_BY_CALCULATED_BY: Record<CalculatedBy, string> = {
  PERCENTAGE: '%',
  FLAT_AMOUNT: '$',
};

export const getRangesLabel = (awardType: string): string =>
  RANGES_LABEL_BY_AWARD_TYPE[awardType as AwardType] ?? '';

export const getSecondaryLabel = (calculatedBy: string): string =>
  SECONDARY_LABEL_BY_CALCULATED_BY[calculatedBy as CalculatedBy] ?? '%';

export interface Range {
  from: number | null;
  to: number | null;
  nonAid?: boolean;
}

/**
 * Loose form accepted by helpers below; values may arrive as `undefined` from
 * partial calculation rows or unsanitized API payloads. Helpers normalize
 * undefined → null at runtime via `??`/`==`.
 */
export type RangeLike = {
  from?: number | null;
  to?: number | null;
  nonAid?: boolean;
};

export interface CalculationRow {
  range?: RangeLike | null;
  academicRankId?: number;
  amount?: number;
  discount?: number;
  nonAid?: boolean | null;
}

/**
 * A Pell regime carries one extra null-range cell per rank alongside the
 * ordinary Non-Aid passthrough cell (`RegimeCalculationDto.nonAid`), so two
 * cells can share identical `{from: null, to: null}` bounds. The explicit
 * `nonAid === false` suffix keeps their keys from colliding; every other
 * range (nonAid omitted/true, or a real numeric range) keeps its original
 * key shape for backward compatibility.
 */
export const buildRangeKey = (range: RangeLike): string => {
  const from = range.from ?? 'null';
  const to = range.to ?? 'null';
  const isPellCell = from === 'null' && to === 'null' && range.nonAid === false;

  return `${from}_${to}${isPellCell ? '_pell' : ''}`;
};

export const isNonAidRange = (range: RangeLike): boolean => {
  if (range.nonAid != null) return range.nonAid;

  return range.from == null && range.to == null;
};

export const isPellRange = (range: RangeLike): boolean =>
  range.from == null && range.to == null && range.nonAid === false;

export const sortRangesForDisplay = (ranges: Range[]): Range[] => {
  const nonAid = ranges.filter(isNonAidRange);
  const pell = ranges.filter((r) => !isNonAidRange(r) && isPellRange(r));
  const numeric = ranges
    .filter((r) => !isNonAidRange(r) && !isPellRange(r))
    .sort((a, b) => (a.from ?? -Infinity) - (b.from ?? -Infinity));

  return [...nonAid, ...pell, ...numeric];
};

export const formatRangeLabel = (range: Range): string => {
  if (isNonAidRange(range)) return 'Non-Aid';
  if (isPellRange(range)) return 'Pell Recipients';

  const from = range.from != null ? range.from.toLocaleString() : '';
  const to = range.to != null ? range.to.toLocaleString() : '';

  // Open-ended ranges represent ±infinity: `from`-only is "this value and up"
  // (> from), `to`-only is "up to this value" (< to). For negative bounds the
  // legacy trailing-dash form (`$ -100-`) was ambiguous, so use explicit
  // comparison operators and "to" as the bounded-range separator. A single `$`
  // prefixes the label rather than repeating before every number.
  if (from && to) return `$ ${from} to ${to}`;
  if (from) return `> $ ${from}`;
  if (to) return `< $ ${to}`;

  return '';
};

export const getUniqueRangesFromCalculation = (
  calculation: CalculationRow[] = []
): Range[] => {
  const seen = new Set<string>();

  return calculation.reduce<Range[]>((acc, row) => {
    if (!row.range) return acc;

    const range: Range = {
      from: row.range.from ?? null,
      to: row.range.to ?? null,
      nonAid: row.nonAid ?? undefined,
    };
    const key = buildRangeKey(range);

    if (!seen.has(key)) {
      seen.add(key);
      acc.push(range);
    }

    return acc;
  }, []);
};
